// Story 1.19 — /account/danger-zone/delete page.
//
// Composes the four account-deletion feature components based on
// useAccountDeletionStatus().data:
//   * data === null            → IneligibilityChecklist (if last submit
//                                gave 422) + DeleteAccountModal trigger
//   * data.status === 'pending'    → PendingDeletionPanel
//   * data.status === 'processing' → PendingDeletionPanel (read-only;
//                                    cancel is no-op because the worker
//                                    has already claimed the row)
//   * data.status === 'completed'  → CompletedDeletionPanel
//   * data.status === 'cancelled'  → DeleteAccountModal trigger with a
//                                    small "Previous request cancelled" note
//
// Deep-link-only at v1 (no top-nav link); the account-settings tab
// navigation that surfaces this page lands when account-settings tabs
// ship as a future story.

import { useState } from "react";
import { Button } from "@greenhold/components";

import { CompletedDeletionPanel } from "../features/account-deletion/CompletedDeletionPanel";
import { DeleteAccountModal } from "../features/account-deletion/DeleteAccountModal";
import { IneligibilityChecklist } from "../features/account-deletion/IneligibilityChecklist";
import { PendingDeletionPanel } from "../features/account-deletion/PendingDeletionPanel";
import { useAccountDeletionStatus } from "../features/account-deletion/useAccountDeletionStatus";
import type { DeletionError } from "../features/account-deletion/useRequestDeletion";

export function DangerZonePage() {
  const status = useAccountDeletionStatus();
  const [modalOpen, setModalOpen] = useState(false);
  const [ineligibility, setIneligibility] = useState<DeletionError | null>(null);

  if (status.isLoading) {
    return (
      <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 p-6 text-brand-paper-100">
        <p className="font-body text-sm opacity-80">Loading…</p>
      </main>
    );
  }

  const data = status.data?.data ?? null;
  const isPending = data?.status === "pending" || data?.status === "processing";
  const isCompleted = data?.status === "completed";

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 p-6 text-brand-paper-100">
      <header>
        <h1 className="font-heading text-4xl font-bold">
          Danger zone — delete your account
        </h1>
        <p className="font-body text-sm opacity-80">
          Leaving Greenhold is as deliberate as joining it. No friction, no
          dark patterns.
        </p>
      </header>

      {isPending && data && <PendingDeletionPanel status={data} />}

      {isCompleted && data && <CompletedDeletionPanel status={data} />}

      {!isPending && !isCompleted && (
        <section className="space-y-4">
          {ineligibility && <IneligibilityChecklist detail={ineligibility} />}
          {data?.status === "cancelled" && (
            <p className="text-sm text-muted-foreground">
              Previous deletion request cancelled. You can submit a new one.
            </p>
          )}
          <div className="rounded-lg border border-border bg-card/40 p-6 space-y-4">
            <h2 className="font-heading text-lg font-semibold">
              Delete this account
            </h2>
            <p className="font-body text-sm">
              This kicks off a 24-hour window before final anonymization.
              You'll be able to cancel from this page right up until then.
            </p>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setModalOpen(true)}
            >
              Delete my account…
            </Button>
          </div>
        </section>
      )}

      <DeleteAccountModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onIneligibility={(detail) => setIneligibility(detail)}
      />
    </main>
  );
}
